"use client";

import { Loader2, ShoppingCart } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";

import {
  cartApi,
  selectCartItemIdByVariantId,
  selectIsVariantInCart,
} from "../cartApi";
import { useCart } from "../hooks/useCart";
import { registerGroupedVariantParents } from "../groupedVariantParents";
import { ROUTES } from "@/src/constants/routes";
import { cn } from "@/src/lib/utils/cn";
import { useAppSelector } from "@/src/store/hooks";

type AddToCartButtonProps = {
  variantId: number | null;
  variantIds?: number[];
  parentVariantId?: number;
  isAvailable: boolean;
  className?: string;
  iconOnly?: boolean;
  ariaLabel?: string;
  label?: string;
  mode?: "toggle" | "add";
  redirectToCartOnSuccess?: boolean;
};

const addToCartButtonClass =
  "inline-flex flex-1 items-center justify-center gap-[0.65rem] rounded-full border border-[rgba(238,49,106,0.42)] bg-[var(--color-surface-strong)]  py-2 text-[0.95rem] font-semibold text-[var(--color-ink)] transition hover:-translate-y-[2px] hover:bg-[var(--color-paper)]";

export default function AddToCartButton({
  variantId,
  variantIds,
  parentVariantId,
  isAvailable,
  className,
  iconOnly = false,
  ariaLabel,
  label = "السلة",
  mode = "toggle",
  redirectToCartOnSuccess = false,
}: AddToCartButtonProps) {
  const router = useRouter();
  const [isPressed, setIsPressed] = useState(false);
  const [isJustAdded, setIsJustAdded] = useState(false);
  const justAddedTimerRef = useRef<number | null>(null);
  const clickLockRef = useRef(false);
  const [isPending, startTransition] = useTransition();
  const isInCartSelector = useMemo(
    () =>
      typeof variantId === "number" ? selectIsVariantInCart(variantId) : null,
    [variantId],
  );
  const cartItemIdSelector = useMemo(
    () =>
      typeof variantId === "number"
        ? selectCartItemIdByVariantId(variantId)
        : null,
    [variantId],
  );
  const isInCart = useAppSelector((state) =>
    isInCartSelector ? isInCartSelector(state) : false,
  );
  const cartItemId = useAppSelector((state) =>
    cartItemIdSelector ? cartItemIdSelector(state) : null,
  );

  // Guest and authenticated carts use the same backend-backed query. Keeping
  // this subscription active makes every card react to the shared cache.
  cartApi.useGetCartQuery(undefined, {
    skip: typeof variantId !== "number",
    selectFromResult: () => ({}),
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { addItems, toggleItemByVariant, isMutating, mutationState } = useCart({
    autoLoad: false,
  });
  const isAddMode = mode === "add";
  const targetVariantIds = useMemo(
    () =>
      Array.from(new Set(variantIds ?? (variantId ? [variantId] : []))).filter(
        (id) => Number.isInteger(id) && id > 0,
      ),
    [variantId, variantIds],
  );
  const isAddingThisVariant =
    mutationState?.type === "add" && mutationState.variantId === variantId;
  const isRemovingThisVariant =
    mutationState?.type === "remove" && mutationState.itemId === cartItemId;
  const isActiveVisual =
    isPressed ||
    isJustAdded ||
    isPending ||
    isAddingThisVariant ||
    Boolean(isRemovingThisVariant) ||
    (!isAddMode && isInCart) ||
    (iconOnly && !isAddMode && isInCart);

  useEffect(() => {
    return () => {
      if (justAddedTimerRef.current !== null) {
        window.clearTimeout(justAddedTimerRef.current);
      }
    };
  }, []);

  const isDisabled =
    !isAvailable || targetVariantIds.length === 0 || isPending || isMutating;
  const defaultAriaLabel = isAvailable
    ? isInCart
      ? "حذف المنتج من السلة"
      : "أضف المنتج إلى السلة"
    : "المنتج غير متاح الآن";
  const buttonAriaLabel =
    ariaLabel ?? (iconOnly ? defaultAriaLabel : undefined);
  const cartIconClassName = cn("h-4 w-4", isActiveVisual ? "fill-current" : "");

  return (
    <button
      type="button"
      className={cn(
        addToCartButtonClass,
        "disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:translate-y-0",
        className,
        isActiveVisual
          ? "!border-[#ee376a] !bg-[#ee376a] !text-white ring-2 ring-[#ee376a] ring-offset-2 ring-offset-white"
          : "",
      )}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-label={buttonAriaLabel}
      onPointerDown={() => setIsPressed(true)}
      onPointerUp={() => setIsPressed(false)}
      onPointerLeave={() => setIsPressed(false)}
      onBlur={() => setIsPressed(false)}
      onClick={async (event) => {
        event.stopPropagation();

        if (
          !isAvailable ||
          targetVariantIds.length === 0 ||
          clickLockRef.current
        ) {
          return;
        }

        clickLockRef.current = true;
        setIsJustAdded(true);
        if (justAddedTimerRef.current !== null) {
          window.clearTimeout(justAddedTimerRef.current);
        }
        justAddedTimerRef.current = window.setTimeout(() => {
          setIsJustAdded(false);
        }, 700);

        startTransition(() => {
          setIsPressed(false);
        });

        try {
          let success = true;

          if (isAddMode && parentVariantId) {
            registerGroupedVariantParents(targetVariantIds, parentVariantId);
          }

          if (isAddMode) {
            success = await addItems(targetVariantIds);
          } else {
            success = await toggleItemByVariant(targetVariantIds[0]);
          }

          if (success && redirectToCartOnSuccess) {
            const highlightParam = encodeURIComponent(
              targetVariantIds.join(","),
            );

            window.setTimeout(() => {
              router.push(`${ROUTES.CART}?highlight=${highlightParam}`);
            }, 1000);
          }
        } finally {
          clickLockRef.current = false;
          setIsPressed(false);
        }
      }}
    >
      {isAvailable ? (
        isPending || isAddingThisVariant ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {iconOnly ? null : label}
          </span>
        ) : iconOnly ? (
          <ShoppingCart className={cartIconClassName} />
        ) : isInCart ? (
          <span className="inline-flex items-center gap-2">
            <ShoppingCart className={cartIconClassName} />
            <span>{label}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-2">
            <ShoppingCart className={cartIconClassName} />
            <span>{label}</span>
          </span>
        )
      ) : iconOnly ? (
        <ShoppingCart className={cartIconClassName} />
      ) : (
        "غير متاح الآن"
      )}
    </button>
  );
}
